import React from 'react'
import { Grid } from '@mui/material'
import { CertificatesTeaser } from 'components/pages/certificates/CertificatesTeaser'
import { PendingSaleTeaser } from 'components/pages/certificates/pendingSale'
import type { PendingSale } from 'types'

export type CertificatesLayout = 'list' | 'grid'

/**
 * The column span of ONE card, per breakpoint — the single expression behind
 * both the real grid and its skeleton.
 *
 * Exported rather than written inline because the loading state has to occupy
 * the same footprint the cards will (working-conventions §19), and "the same"
 * is only guaranteed if there is one expression rather than two that currently
 * agree. The skeleton shipped as a single column of ten 160px bars against a
 * 2-up/3-up grid of ~200px cards, so the page visibly re-laid itself out on
 * every first load.
 *
 * ⚠️ The layout toggle no longer changes the COLUMN COUNT anywhere — it changes
 * the CARD. At `xs` both layouts are one card per row; grid differs by being
 * `compact` (image on top, square, the card's full width) rather than a row with
 * a 128px thumbnail at its left.
 *
 * Grid used to be 2-up at `xs`, and the card that produced is the one every
 * constraint in `CertificatesTeaser` was sized against: a 163.5px shell whose
 * title box is 137.5px, where "Louis Vuitton Monogram Jacket" spends both of its
 * clamped lines and a status badge cannot share the title row. One per row makes
 * the phone card ~343px — the image is then the card, which is the whole reason
 * to pick grid over list on a device you hold.
 *
 * It also returns the activity strip at `xs`. VERA-315 dropped it from `compact`
 * because the activity block is the card's entire height variance (measured: a
 * 199px spread across a 2-up grid, falling to 28px once dropped) and ragged
 * pairs are what a grid cannot absorb. That reasoning is about cards SHARING a
 * row, so it expires exactly where this does — see `gridSharesRow` in the card.
 */
export const certificateGridItemSpan = (_layout: CertificatesLayout) =>
  ({ xs: 12, sm: 6, md: 4 }) as const

/**
 * The gap between cards, in MUI spacing units.
 *
 * `apps/app` is on MUI's default 8px base (working-conventions §29), so this is
 * 16px — the same 16 `CONTENT_INNER_PAD` names as the list's inner padding.
 * Shared with the skeleton for the reason above.
 */
export const CERTIFICATE_GRID_SPACING = 2

/**
 * How long the grid takes to settle to its new contents after a filter, sort or
 * search change.
 *
 * 180ms on the app's own page-slide curve (`cubic-bezier(0.32, 0.72, 0, 1)`,
 * the same one `ToolbarControl`'s clear segment and the tab transition use) —
 * shorter than those, because this is a state change on content the reader is
 * already looking at rather than a surface arriving. Long enough to read as a
 * settle, short enough that a filter still feels immediate.
 */
const GRID_SWAP_MS = 180
const GRID_SWAP_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)'

/** How far the grid dims while the incoming set is in flight. */
const GRID_PENDING_OPACITY = 0.55

/**
 * VERA-304 — a certificate that ARRIVED (an incoming gift/transfer picked up by
 * the 20s poll) animates into its slot at the top of the list.
 *
 * The motion vocabulary is the app's own, not a new one: 260ms
 * (`PAGE_SLIDE_MS`) on `cubic-bezier(0.32, 0.72, 0, 1)`, the curve the page
 * push, the sheet step, the toolbar clear and the grid swap above all share.
 *
 * It descends (`-10px` -> `0`) rather than rising like `veraStateCardIn`'s
 * `+8px`, because this card is being INSERTED at the top and pushing the list
 * down: moving with that displacement reads as arriving into the slot, moving
 * against it reads as two things fighting.
 *
 * ⚠️ `forwards`, NOT `both` — the same failure mode `pageTransition.ts` documents
 * at length. Backwards fill applies the FROM keyframe (`opacity: 0`) from the
 * moment the style attaches, so anywhere animations do not tick — notably the
 * dev preview pane, which reports `visibilityState: hidden` and never runs them
 * — the card would be permanently INVISIBLE rather than merely un-animated. With
 * `forwards` the worst case degrades to "it just appears", which is the old
 * behaviour and always correct.
 */
const ARRIVAL_MS = 260
const ARRIVAL_SX = {
  '@keyframes veraCertificateArriveIn': {
    from: { opacity: 0, transform: 'translate3d(0, -10px, 0)' },
    to: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
  },
  animation: `veraCertificateArriveIn ${ARRIVAL_MS}ms ${GRID_SWAP_EASING} forwards`,
  willChange: 'transform, opacity',
  // A card sliding into view is exactly the movement this setting exists to
  // suppress. `none` and not a shorter duration: the arrival is still correct
  // without motion, since the row is already hoisted to the top either way.
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const

export type CertificatesGridProps = {
  /** `list` = one full-width card per row; `grid` = two compact cards per row. */
  layout?: CertificatesLayout
  certificates: Array<any>
  onRefetch: () => void
  selectMode: boolean
  isSelected: (id: string) => boolean
  onToggleSelected: (id: string) => void
  displayedData: Record<string, boolean>
  /**
   * The rows currently rendered belong to the PREVIOUS filter/sort key, and the
   * incoming set is still in flight.
   *
   * Must be react-query's `isPlaceholderData` and NOT `isFetching`/`isRefetching`.
   * The list polls the same key every 20s (see `useGetCertificates`), and those
   * flags are true on every one of those polls — driving the transition from them
   * would make the whole grid pulse every 20 seconds, which is the same class of
   * bug as the skeleton remount this transition replaced. `isPlaceholderData` is
   * true only for a key CHANGE, which is exactly the event worth animating.
   */
  pending?: boolean
  /**
   * Certificate ids that arrived on the LAST poll and should animate in.
   *
   * Deliberately a set of ids handed in from outside rather than anything this
   * component derives: "new" is a statement about the PREVIOUS poll's contents,
   * which only a caller holding that history can make. See
   * `useArrivingTransfers`.
   */
  arrivingIds?: Set<string>
  /**
   * VRG-455 — items the buyer has BOUGHT but not yet claimed, rendered as the
   * first cards in this grid.
   *
   * IN the grid, not pinned above it. VERA-304 removed pinned banners from this
   * page because four of them walled off the list, and a banner reads as a
   * notice about something elsewhere — but these are things the buyer owns, so
   * they belong among the things they own. Rendering them here also means they
   * inherit the grid's own column spans, gap and swap transition for free,
   * rather than growing a second layout that has to be kept in step.
   *
   * FIRST, unconditionally, and never sorted into the list: they carry no
   * status, no certificate and no last-activity date, so every sort key this
   * page offers is undefined for them. A row that cannot participate in the
   * ordering has to sit outside it, and the top is where the one row that wants
   * an action belongs.
   */
  pendingSales?: PendingSale[]
}

export const CertificatesGrid: React.FC<CertificatesGridProps> = ({
  layout = 'list',
  certificates,
  onRefetch,
  selectMode,
  isSelected,
  onToggleSelected,
  displayedData,
  pending = false,
  arrivingIds,
  pendingSales = [],
}) => {
  return (
    <Grid
      container
      spacing={CERTIFICATE_GRID_SPACING}
      /*
       * The cards used to be REPLACED — a filter change asked for a query key
       * with no cached data, so the page tore the grid down to the skeleton and
       * rebuilt it. `keepPreviousData` keeps them mounted; this is what makes the
       * handover read as one movement instead of a cut.
       *
       * Opacity ONLY. Not height, not transform: the incoming set almost always
       * has a different row count, so animating anything that affects layout
       * would move the rest of the page while the reader is looking at it — and
       * the pending state has no known duration, so a layout animation would have
       * to guess an end state it cannot know.
       *
       * `pointerEvents` off while pending, so a card cannot be tapped during the
       * handover and open a certificate that the new filter is about to exclude.
       */
      sx={{
        opacity: pending ? GRID_PENDING_OPACITY : 1,
        pointerEvents: pending ? 'none' : 'auto',
        transition: `opacity ${GRID_SWAP_MS}ms ${GRID_SWAP_EASING}`,
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
      aria-busy={pending || undefined}
    >
      {/* Bought, waiting on a scan. Same cell span as a certificate card, so a
          row never mixes two card widths. */}
      {pendingSales.map(sale => (
        <Grid item {...certificateGridItemSpan(layout)} key={`pending-sale-${sale.id}`}>
          <PendingSaleTeaser
            sale={sale}
            compact={layout === 'grid'}
          />
        </Grid>
      ))}
      {certificates?.map((certificate: any) => (
        <Grid
          item
          {...certificateGridItemSpan(layout)}
          key={certificate.id}
          /* On the GRID ITEM, not on the card: the item is the box the layout
             already reserves, so animating it cannot change the footprint and
             the surrounding cards never move because of the animation. */
          sx={arrivingIds?.has(String(certificate.id)) ? ARRIVAL_SX : undefined}
        >
          <CertificatesTeaser
            certificate={certificate}
            refetchCertificates={onRefetch}
            selectMode={selectMode}
            selected={isSelected(certificate.id)}
            onToggleSelected={onToggleSelected}
            displayedData={displayedData}
            compact={layout === 'grid'}
          />
        </Grid>
      ))}
    </Grid>
  )
}

export default CertificatesGrid


